import { useState } from 'react'
import { cn } from '@/lib/utils'
import { ChevronDown, ChevronRight } from 'lucide-react'

type ModelEntry = {
  role: string
  provider: string
  model: string
  file: string
  notes?: string
}

type ModelSection = {
  title: string
  entries: ModelEntry[]
}

const MODEL_SECTIONS: ModelSection[] = [
  {
    title: 'LLM',
    entries: [
      { role: 'Workflow Agent (primary)', provider: 'Anthropic', model: 'claude-sonnet-4-6', file: 'workflowAgent.ts', notes: 'Overridable via agent_model flag' },
      { role: 'Video Analysis (deep)', provider: 'Anthropic', model: 'claude-opus-4-6', file: 'videoAnalysis.ts' },
      { role: 'Video Analysis (batch)', provider: 'Anthropic', model: 'claude-sonnet-4-6', file: 'videoAnalysis.ts' },
{ role: 'Classification / Routing', provider: 'Anthropic', model: 'claude-haiku-4-5-20251001', file: 'workflowAgent.ts', notes: 'Lightweight tasks: intent detection, sentiment' },
      { role: 'LLM Failover', provider: 'OpenRouter', model: 'anthropic/{model}', file: 'llm.ts', notes: 'Auto-activates on 429/529; also via openrouter_failover flag' },
    ],
  },
  {
    title: 'Vision / Analysis',
    entries: [
      { role: 'Media Analysis & Transcription', provider: 'Google', model: 'gemini-2.0-flash', file: 'geminiProxy.ts' },
    ],
  },
  {
    title: 'Video Generation',
    entries: [
      { role: 'Text-to-Video (Kling V3)', provider: 'Fal', model: 'fal-ai/kling-video/v3/pro/text-to-video', file: 'aiModels.ts' },
      { role: 'Image-to-Video (Kling V3)', provider: 'Fal', model: 'fal-ai/kling-video/v3/pro/image-to-video', file: 'aiModels.ts' },
      { role: 'Text-to-Video (Kling O3)', provider: 'Fal', model: 'fal-ai/kling-video/o3/pro/text-to-video', file: 'aiModels.ts' },
      { role: 'Image-to-Video (Kling O3)', provider: 'Fal', model: 'fal-ai/kling-video/o3/pro/image-to-video', file: 'aiModels.ts' },
      { role: 'Reference-to-Video (Kling O3)', provider: 'Fal', model: 'fal-ai/kling-video/o3/pro/reference-to-video', file: 'aiModels.ts' },
      { role: 'Video Edit (Kling O3)', provider: 'Fal', model: 'fal-ai/kling-video/o3/pro/video-to-video/edit', file: 'aiModels.ts' },
      { role: 'Video Reference (Kling O3)', provider: 'Fal', model: 'fal-ai/kling-video/o3/pro/video-to-video/reference', file: 'aiModels.ts' },
      { role: 'Text-to-Video (Veo 3.1)', provider: 'Fal', model: 'fal-ai/veo3.1/fast', file: 'aiModels.ts', notes: 'Default provider via video_provider flag' },
      { role: 'Image-to-Video (Veo 3.1)', provider: 'Fal', model: 'fal-ai/veo3.1/fast/image-to-video', file: 'aiModels.ts' },
      { role: 'Reference-to-Video (Veo 3.1)', provider: 'Fal', model: 'fal-ai/veo3.1/fast/reference-to-video', file: 'aiModels.ts' },
      { role: 'First/Last Frame (Veo 3.1)', provider: 'Fal', model: 'fal-ai/veo3.1/fast/first-last-frame-to-video', file: 'aiModels.ts' },
      { role: 'Extend Video (Veo 3.1)', provider: 'Fal', model: 'fal-ai/veo3.1/fast/extend-video', file: 'aiModels.ts' },
      { role: 'Text-to-Video (Grok)', provider: 'Fal', model: 'xai/grok-imagine-video/text-to-video', file: 'aiModels.ts', notes: 'Relaxed content policy fallback' },
      { role: 'Image-to-Video (Grok)', provider: 'Fal', model: 'xai/grok-imagine-video/image-to-video', file: 'aiModels.ts' },
      { role: 'Edit Video (Grok)', provider: 'Fal', model: 'xai/grok-imagine-video/edit-video', file: 'aiModels.ts' },
    ],
  },
  {
    title: 'Image Generation',
    entries: [
      { role: 'Clip Previews', provider: 'Fal', model: 'fal-ai/flux-2/turbo', file: 'imagen.ts' },
      { role: 'Clip Preview Edit', provider: 'Fal', model: 'fal-ai/flux-2/turbo/edit', file: 'imagen.ts' },
      { role: 'Keyframes (with refs)', provider: 'Fal', model: 'fal-ai/flux-pro/v1.1/redux', file: 'keyframeGeneration.ts' },
      { role: 'Keyframes (text-only)', provider: 'Fal', model: 'fal-ai/flux-pro/v1.1', file: 'keyframeGeneration.ts' },
      { role: 'Storyboard Sketches', provider: 'Fal', model: 'fal-ai/flux/schnell', file: 'workflowAgent.ts' },
      { role: 'Image Upscaling', provider: 'Fal', model: 'fal-ai/esrgan', file: 'workflowAgent.ts, assets.ts' },
    ],
  },
  {
    title: 'Audio & Speech',
    entries: [
      { role: 'TTS (primary)', provider: 'Fal', model: 'fal-ai/elevenlabs/tts/eleven-v3', file: 'aiModels.ts' },
      { role: 'TTS (multilingual)', provider: 'Fal', model: 'fal-ai/elevenlabs/tts/multilingual-v2', file: 'aiModels.ts' },
      { role: 'Multi-speaker Dialogue', provider: 'Fal', model: 'fal-ai/elevenlabs/text-to-dialogue/eleven-v3', file: 'aiModels.ts' },
      { role: 'Sound Effects', provider: 'Fal', model: 'fal-ai/elevenlabs/sound-effects/v2', file: 'audioGeneration.ts' },
      { role: 'Background Music', provider: 'Fal', model: 'fal-ai/stable-audio', file: 'audioGeneration.ts', notes: 'Up to 47s' },
      { role: 'Video-synced Audio', provider: 'Fal', model: 'fal-ai/mmaudio-v2', file: 'audioGeneration.ts', notes: 'Analyzes video for matching audio, max 30s' },
      { role: 'Cinematic Music', provider: 'Fal', model: 'fal-ai/lyria2', file: 'audioGeneration.ts', notes: 'Orchestral/cinematic, ~30s' },
      { role: 'Music with Vocals', provider: 'Fal', model: 'fal-ai/minimax-music/v2', file: 'audioGeneration.ts', notes: 'Supports lyrics, ~60s' },
      { role: 'AI Avatar', provider: 'Fal', model: 'veed/fabric-1.0', file: 'aiModels.ts', notes: 'Native lip sync' },
      { role: 'Voice Cloning', provider: 'Fal', model: 'fal-ai/kling-video/create-voice', file: 'aiModels.ts' },
      { role: 'Transcription', provider: 'Fal', model: 'fal-ai/whisper', file: 'aiModels.ts', notes: 'Word-level timestamps' },
    ],
  },
]

const PROVIDER_COLORS: Record<string, string> = {
  'Anthropic': 'bg-amber-50 text-amber-700 border-amber-200',
  'OpenRouter': 'bg-violet-50 text-violet-700 border-violet-200',
  'Google': 'bg-blue-50 text-blue-700 border-blue-200',
  'Fal': 'bg-emerald-50 text-emerald-700 border-emerald-200',
}

function ProviderBadge({ provider }: { provider: string }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border',
      PROVIDER_COLORS[provider] ?? 'bg-slate-100 text-slate-600 border-slate-200'
    )}>
      {provider}
    </span>
  )
}

function SectionCard({ section, defaultOpen = true }: { section: ModelSection; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <h3 className="text-sm font-semibold text-slate-800">{section.title}</h3>
          <span className="text-xs text-slate-400">{section.entries.length} models</span>
        </div>
      </button>
      {open && (
        <div className="divide-y divide-slate-100">
          <div className="grid grid-cols-[1fr_140px_1fr] gap-4 px-4 py-2 text-[11px] font-medium text-slate-400 uppercase tracking-wider border-b border-slate-200 bg-slate-50/50">
            <span>Role</span>
            <span>Inference Provider</span>
            <span>Model</span>
          </div>
          {section.entries.map((entry, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_140px_1fr] gap-4 px-4 py-2.5 hover:bg-slate-50 transition-colors items-center"
            >
              <div>
                <span className="text-sm text-slate-700">{entry.role}</span>
                {entry.notes && (
                  <p className="text-xs text-slate-400 mt-0.5">{entry.notes}</p>
                )}
              </div>
              <div>
                <ProviderBadge provider={entry.provider} />
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <code className="text-xs text-slate-600 font-mono truncate">{entry.model}</code>
                <span className="text-[10px] text-slate-300 shrink-0">{entry.file}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function AdminModelConfigPage() {
  const totalModels = MODEL_SECTIONS.reduce((sum, s) => sum + s.entries.length, 0)
  const providers = [...new Set(MODEL_SECTIONS.flatMap(s => s.entries.map(e => e.provider)))]

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Model Configuration</h1>
        <p className="text-sm text-slate-500 mt-1">
          {totalModels} models across {providers.length} providers — {providers.join(', ')}
        </p>
      </div>

      <div className="space-y-4">
        {MODEL_SECTIONS.map((section) => (
          <SectionCard key={section.title} section={section} />
        ))}
      </div>
    </div>
  )
}
